import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import StatusInfo from '../components/StatusInfo';
import useIsMobile from '../hooks/useIsMobile';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const TAX_LABEL = { self_employed: 'Самозанятый', individual_entrepreneur: 'ИП' };
const VERIFICATION_LABEL = { new: 'Новый', pending: 'На проверке', active: 'Верифицирован', rejected: 'Отклонён' };
const VERIFICATION_BADGE_STYLE = { new: 'badgeGray', pending: 'badgeYellow', active: 'badgeCheck', rejected: 'badgeRedSoft' };

const emptyForm = {
  last_name: '', first_name: '', middle_name: '', birth_date: '', phone: '', specialty: '', tax_status: 'self_employed',
  inn: '', bank_name: '', bank_bik: '', bank_account: '', bank_corr_account: '', ogrnip: '',
};
const REQUEST_FIELD_LABELS = {
  last_name: 'Фамилия', first_name: 'Имя', middle_name: 'Отчество', birth_date: 'Дата рождения',
  phone: 'Телефон', specialty: 'Специальность', tax_status: 'Налоговый статус',
  inn: 'ИНН', bank_name: 'Банк', bank_bik: 'БИК банка', bank_account: 'Расчётный счёт',
  bank_corr_account: 'Корр. счёт', ogrnip: 'ОГРНИП',
};
// поля реквизитов: общие для обоих статусов + доп. для ИП (ОГРНИП)
const PAYOUT_FIELDS = [
  ['inn', 'ИНН', '12 цифр'],
  ['bank_name', 'Банк', 'Название банка'],
  ['bank_bik', 'БИК банка', '9 цифр'],
  ['bank_account', 'Расчётный счёт', '20 цифр'],
  ['bank_corr_account', 'Корреспондентский счёт', '20 цифр'],
];

function formFromDoctor(d) {
  return {
    last_name: d.last_name || '', first_name: d.first_name || '', middle_name: d.middle_name || '',
    birth_date: d.birth_date ? d.birth_date.slice(0, 10) : '',
    phone: d.phone || '', specialty: d.specialty || '',
    tax_status: d.tax_status || '',
    inn: d.inn || '', bank_name: d.bank_name || '', bank_bik: d.bank_bik || '',
    bank_account: d.bank_account || '', bank_corr_account: d.bank_corr_account || '', ogrnip: d.ogrnip || '',
  };
}

export default function Profile() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [doctor, setDoctor] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [profileRequest, setProfileRequest] = useState(null);
  const [notifBusy, setNotifBusy] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [publicIdCopied, setPublicIdCopied] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState('');
  const [pwModal, setPwModal] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwNew2, setPwNew2] = useState('');
  const [pwErr, setPwErr] = useState('');
  const [pwMsg, setPwMsg] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => Promise.all([
    axios.get(`${API}/auth/me`, { headers }),
    axios.get(`${API}/auth/me/profile-request`, { headers }).catch(() => ({ data: null })),
  ])
    .then(([r, rq]) => {
      setDoctor(r.data);
      setForm(formFromDoctor(r.data));
      setProfileRequest(rq.data);
    })
    .catch(() => { localStorage.clear(); navigate('/login'); });
  useEffect(() => { load(); }, []); // eslint-disable-line

  const logout = () => { localStorage.clear(); navigate('/login'); };
  const toggleEmailNotifications = async () => {
    if (notifBusy) return;
    const next = !doctor.email_notifications;
    setNotifBusy(true);
    try {
      await axios.patch(`${API}/auth/me/email-notifications`, { enabled: next }, { headers });
      setDoctor({ ...doctor, email_notifications: next });
    } catch { /* тумблер просто не переключится — доктор увидит прежнее состояние */ }
    setNotifBusy(false);
  };

  const isPending = profileRequest && profileRequest.status === 'pending';

  const save = async () => {
    setSaving(true); setErr('');
    try { await axios.patch(`${API}/auth/me/request`, form, { headers }); setEdit(false); load(); }
    catch (e) { setErr(e?.response?.data?.detail || 'Не удалось отправить заявку'); }
    setSaving(false);
  };
  const cancel = () => { setEdit(false); setErr(''); if (doctor) setForm(formFromDoctor(doctor)); };

  const uploadDoc = async () => {
    if (!uploadFile) { setUploadErr('Выберите файл'); return; }
    setUploading(true); setUploadErr('');
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      await axios.post(`${API}/auth/me/verification-document`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setUploadFile(null);
      load();
    } catch (e) { setUploadErr(e?.response?.data?.detail || 'Не удалось загрузить документ'); }
    setUploading(false);
  };
  const viewDoc = async () => {
    try {
      const r = await axios.get(`${API}/auth/me/verification-document`, { headers, responseType: 'blob' });
      window.open(window.URL.createObjectURL(r.data), '_blank');
    } catch { setUploadErr('Не удалось открыть документ'); }
  };

  const openPwModal = () => {
    setPwModal(true); setPwCurrent(''); setPwNew(''); setPwNew2(''); setPwErr(''); setPwMsg('');
  };
  const closePwModal = () => setPwModal(false);
  const changePassword = async () => {
    setPwErr(''); setPwMsg('');
    if (!pwCurrent || !pwNew) { setPwErr('Заполните оба пароля'); return; }
    if (pwNew.length < 6) { setPwErr('Новый пароль должен быть не короче 6 символов'); return; }
    if (pwNew !== pwNew2) { setPwErr('Пароли не совпадают'); return; }
    setPwSaving(true);
    try {
      await axios.patch(`${API}/auth/me/password`, { current_password: pwCurrent, new_password: pwNew }, { headers });
      setPwMsg('Пароль изменён'); setPwCurrent(''); setPwNew(''); setPwNew2('');
    } catch (e) { setPwErr(e?.response?.data?.detail || 'Не удалось изменить пароль'); }
    setPwSaving(false);
  };

  if (!doctor) return <Layout><div style={s.muted}>Загрузка…</div></Layout>;

  const Row = ({ label, value }) => (
    <div style={s.row}><span style={s.rowLabel}>{label}</span><span style={s.rowValue}>{value || '—'}</span></div>
  );

  return (
    <Layout>
      <div style={{ ...s.topRow, ...(isMobile ? s.topRowMobile : {}) }}>
        <h1 style={s.h1}>Профиль</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <StatusInfo
            title="Как редактируются данные"
            items={[
              ['Что можно менять', 'Личные данные (ФИО, дата рождения, телефон, специальность) и реквизиты для выплат — кнопкой «Редактировать».'],
              ['Модерация', 'Изменения не применяются сразу — уходят заявкой оператору Refero. Так мы защищаем ваши данные и реквизиты выплат от ошибок и подмены.'],
              ['Срок рассмотрения', 'Обычно от 6 до 24 часов в зависимости от загрузки операторов.'],
              ['Пока заявка на рассмотрении', 'Новую отправить нельзя — дождитесь решения по текущей. Если отклонят, увидите причину и сможете отправить заявку заново.'],
            ]}
          />
          {!edit
            ? <button style={s.btnGhost} onClick={() => setEdit(true)} disabled={isPending} title={isPending ? 'Дождитесь рассмотрения текущей заявки' : ''}>Редактировать</button>
            : <div style={{ display: 'flex', gap: 10 }}>
                <button style={s.btnGhost} onClick={cancel}>Отмена</button>
                <button style={s.btnPrimary} onClick={save} disabled={saving}>{saving ? 'Отправка…' : 'Отправить на модерацию'}</button>
              </div>}
        </div>
      </div>

      {isPending && (
        <div style={s.pendingBanner}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Заявка на изменение профиля на рассмотрении</div>
          {Object.entries(profileRequest.changes).map(([field, diff]) => (
            <div key={field} style={{ fontSize: 13 }}>
              {REQUEST_FIELD_LABELS[field] || field}: <span style={{ textDecoration: 'line-through', opacity: 0.7 }}>{diff.old || '—'}</span> → <b>{diff.new || '—'}</b>
            </div>
          ))}
        </div>
      )}
      {profileRequest && profileRequest.status === 'rejected' && !profileRequest.dismissed_at && (
        <div style={{ ...s.rejectedBanner, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>Последняя заявка на изменение профиля отклонена{profileRequest.reject_reason ? `: ${profileRequest.reject_reason}` : ''}. Вы можете отправить новую.</div>
          <button
            onClick={() => {
              axios.post(`${API}/auth/me/profile-request/${profileRequest.id}/dismiss`, {}, { headers }).catch(() => {});
              setProfileRequest({ ...profileRequest, dismissed_at: new Date().toISOString() });
            }}
            style={s.bannerClose}
            aria-label="Закрыть"
          >×</button>
        </div>
      )}

      {/* Шапка */}
      <div style={{ ...s.headerCard, ...(isMobile ? s.headerCardMobile : {}) }}>
        <div style={s.avatar}>{doctor.last_name[0]}{doctor.first_name[0]}</div>
        <div>
          <div style={s.name}>{doctor.last_name} {doctor.first_name} {doctor.middle_name || ''}</div>
          <div style={s.specialty}>{doctor.specialty || 'Специальность не указана'}</div>
          {doctor.public_id && (
            <div style={s.publicIdRow}>
              <span style={s.publicIdLabel}>ID врача</span>
              <span style={s.publicIdValue}>{doctor.public_id}</span>
              <button style={s.publicIdCopy} onClick={() => { navigator.clipboard?.writeText(doctor.public_id); setPublicIdCopied(true); setTimeout(() => setPublicIdCopied(false), 1500); }}>
                {publicIdCopied ? 'Скопировано ✓' : 'Скопировать'}
              </button>
            </div>
          )}
          <div style={s.badges}>
            {doctor.tax_status && <span style={s.badgeTeal}>{TAX_LABEL[doctor.tax_status]}</span>}
            <span style={s[VERIFICATION_BADGE_STYLE[doctor.verification_status] || 'badgeGray']}>
              {doctor.verification_status === 'active' ? '✓ ' : ''}{VERIFICATION_LABEL[doctor.verification_status] || 'Новый'}
            </span>
          </div>
        </div>
      </div>

      <div style={{ ...s.grid, ...(isMobile ? s.gridMobile : {}) }}>
        {/* Личные данные */}
        <div style={s.card}>
          <div style={s.cardTitle}>Личные данные</div>
          <div style={s.row}>
            <span style={s.rowLabel}>Фамилия</span>
            {edit ? <input style={s.inlineInput} value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} />
              : <span style={s.rowValue}>{doctor.last_name}</span>}
          </div>
          <div style={s.row}>
            <span style={s.rowLabel}>Имя</span>
            {edit ? <input style={s.inlineInput} value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} />
              : <span style={s.rowValue}>{doctor.first_name}</span>}
          </div>
          <div style={s.row}>
            <span style={s.rowLabel}>Отчество</span>
            {edit ? <input style={s.inlineInput} value={form.middle_name} onChange={e => setForm({ ...form, middle_name: e.target.value })} />
              : <span style={s.rowValue}>{doctor.middle_name || '—'}</span>}
          </div>
          <div style={s.row}>
            <span style={s.rowLabel}>Дата рождения</span>
            {edit ? <input type="date" style={s.inlineInput} value={form.birth_date} onChange={e => setForm({ ...form, birth_date: e.target.value })} />
              : <span style={s.rowValue}>{new Date(doctor.birth_date).toLocaleDateString('ru')}</span>}
          </div>
          <div style={s.row}>
            <span style={s.rowLabel}>Телефон</span>
            {edit ? <input style={s.inlineInput} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+7 ..." />
              : <span style={s.rowValue}>{doctor.phone || '—'}</span>}
          </div>
          <div style={s.row}>
            <span style={s.rowLabel}>Специальность</span>
            {edit ? <input style={s.inlineInput} value={form.specialty} onChange={e => setForm({ ...form, specialty: e.target.value })} />
              : <span style={s.rowValue}>{doctor.specialty || '—'}</span>}
          </div>
          <Row label="Email" value={doctor.email} />
        </div>

        {/* Реквизиты */}
        <div style={s.card}>
          <div style={s.cardTitle}>Реквизиты для выплат</div>
          {edit ? (
            <>
              <div style={s.taxRow}>
                {['self_employed', 'individual_entrepreneur'].map(k => (
                  <div key={k} style={{ ...s.taxOpt, ...(form.tax_status === k ? s.taxOptActive : {}) }} onClick={() => setForm({ ...form, tax_status: k })}>
                    {TAX_LABEL[k]}
                  </div>
                ))}
              </div>
              {PAYOUT_FIELDS.map(([field, label, placeholder]) => (
                <div key={field} style={s.row}>
                  <span style={s.rowLabel}>{label}</span>
                  <input style={s.inlineInput} value={form[field]} placeholder={placeholder} onChange={e => setForm({ ...form, [field]: e.target.value })} />
                </div>
              ))}
              {form.tax_status === 'individual_entrepreneur' && (
                <div style={s.row}>
                  <span style={s.rowLabel}>ОГРНИП</span>
                  <input style={s.inlineInput} value={form.ogrnip} placeholder="15 цифр" onChange={e => setForm({ ...form, ogrnip: e.target.value })} />
                </div>
              )}
            </>
          ) : (
            <>
              <Row label="Налоговый статус" value={TAX_LABEL[doctor.tax_status]} />
              <Row label="ИНН" value={doctor.inn} />
              <Row label="Банк" value={doctor.bank_name} />
              <Row label="БИК банка" value={doctor.bank_bik} />
              <Row label="Расчётный счёт" value={doctor.bank_account} />
              <Row label="Корр. счёт" value={doctor.bank_corr_account} />
              {doctor.tax_status === 'individual_entrepreneur' && <Row label="ОГРНИП" value={doctor.ogrnip} />}
            </>
          )}
          <div style={s.note}>Используются только для перечисления комиссий.</div>
          {err && <div style={s.error}>{err}</div>}
        </div>

        {/* Верификация ЕГИСЗ */}
        <div style={s.card}>
          <div style={s.cardTitle}>Верификация ЕГИСЗ</div>
          <div style={s.row}>
            <span style={s.rowLabel}>Статус</span>
            <span style={s[VERIFICATION_BADGE_STYLE[doctor.verification_status] || 'badgeGray']}>{VERIFICATION_LABEL[doctor.verification_status] || 'Новый'}</span>
          </div>
          {doctor.verification_status === 'rejected' && doctor.verification_reject_reason && (
            <div style={s.errorSoft}>Причина отказа: {doctor.verification_reject_reason}</div>
          )}
          {doctor.verification_doc_uploaded_at && (
            <div style={s.row}>
              <span style={s.rowLabel}>Загружен</span>
              <span style={s.link} onClick={viewDoc}>Посмотреть документ</span>
            </div>
          )}
          {doctor.verification_status !== 'active' && (
            <>
              <div style={{ ...s.note, marginTop: 12 }}>
                Загрузите выписку ЕГИСЗ об аккредитации (PDF, JPG или PNG, до 10 МБ), чтобы получить доступ к добавлению пациентов.
              </div>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ marginTop: 10 }}
                onChange={e => setUploadFile(e.target.files[0] || null)} />
              {uploadErr && <div style={s.error}>{uploadErr}</div>}
              <button style={{ ...s.btnPrimary, marginTop: 12, width: '100%' }} onClick={uploadDoc} disabled={uploading || !uploadFile}>
                {uploading ? 'Загрузка…' : doctor.verification_status === 'rejected' ? 'Загрузить повторно' : 'Загрузить документ'}
              </button>
            </>
          )}
        </div>

        {/* Уведомления */}
        <div style={s.card}>
          <div style={{ ...s.cardTitle, display: 'flex', alignItems: 'center' }}>
            Уведомления
            <StatusInfo
              title="Советуем включить"
              items={[
                ['Что придёт на почту', 'Начисление и выплата комиссии, новый пациент дошёл до клиники, ответ техподдержки в вашем тикете.'],
                ['Плюс полезное', 'Новости платформы и появление новых клиник/сетей — чтобы не пропустить, куда стало выгоднее направлять пациентов.'],
                ['По умолчанию выключено', 'Включаем только по вашему явному согласию — тумблером ниже. В приложении уведомления (колокольчик) приходят в любом случае.'],
              ]}
            />
          </div>
          <div style={s.toggleRow}>
            <span style={s.rowValue}>Email-уведомления</span>
            <Toggle on={doctor.email_notifications} onClick={toggleEmailNotifications} />
          </div>
        </div>

        {/* Безопасность */}
        <div style={s.card}>
          <div style={s.cardTitle}>Безопасность</div>
          <button style={s.btnGhostFull} onClick={openPwModal}>Сменить пароль</button>
          <button style={s.btnLogout} onClick={logout}>Выйти</button>
        </div>
      </div>

      {pwModal && (
        <div style={s.modalOverlay} onClick={closePwModal}>
          <div style={s.modalCard} onClick={e => e.stopPropagation()}>
            <div style={s.cardTitle}>Сменить пароль</div>
            <div style={{ ...s.row, flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
              <span style={s.rowLabel}>Текущий пароль</span>
              <input type="password" style={s.modalInput} value={pwCurrent} onChange={e => setPwCurrent(e.target.value)} />
            </div>
            <div style={{ ...s.row, flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
              <span style={s.rowLabel}>Новый пароль</span>
              <input type="password" style={s.modalInput} value={pwNew} onChange={e => setPwNew(e.target.value)} placeholder="Не короче 6 символов" />
            </div>
            <div style={{ ...s.row, flexDirection: 'column', alignItems: 'stretch', gap: 6, borderBottom: 'none' }}>
              <span style={s.rowLabel}>Повторите новый пароль</span>
              <input type="password" style={s.modalInput} value={pwNew2} onChange={e => setPwNew2(e.target.value)} />
            </div>
            {pwErr && <div style={s.error}>{pwErr}</div>}
            {pwMsg && <div style={s.successBanner}>{pwMsg}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button style={s.btnGhost} onClick={closePwModal}>Закрыть</button>
              <button style={s.btnPrimary} onClick={changePassword} disabled={pwSaving}>{pwSaving ? 'Сохранение…' : 'Сохранить'}</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Toggle({ on, onClick }) {
  return (
    <div onClick={onClick} style={{ width: 44, height: 24, borderRadius: 12, background: on ? '#2EC4B6' : '#e2e8f0', cursor: 'pointer', position: 'relative', transition: 'background .15s', flexShrink: 0 }}>
      <div style={{ width: 18, height: 18, borderRadius: 9, background: '#fff', position: 'absolute', top: 3, left: on ? 23 : 3, transition: 'left .15s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </div>
  );
}

const s = {
  topRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  topRowMobile: { flexWrap: 'wrap', gap: 12 },
  h1: { color: '#1B2B4B', fontSize: 28, fontWeight: 800, margin: 0 },
  muted: { color: '#64748b', fontSize: 14 },
  btnGhost: { background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', borderRadius: 10, padding: '10px 16px', fontSize: 14, cursor: 'pointer' },
  btnPrimary: { background: '#2EC4B6', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  headerCard: { background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 4px 16px rgba(27,43,75,0.06)', display: 'flex', alignItems: 'center', gap: 20, marginBottom: 20 },
  headerCardMobile: { flexWrap: 'wrap' },
  avatar: { width: 72, height: 72, borderRadius: 36, background: '#e9faf7', color: '#149e90', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, fontWeight: 700, flexShrink: 0 },
  name: { fontSize: 22, fontWeight: 800, color: '#1B2B4B', marginBottom: 4 },
  specialty: { fontSize: 14, color: '#64748b', marginBottom: 8 },
  publicIdRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 },
  publicIdLabel: { fontSize: 11, color: '#94a3b8' },
  publicIdValue: { fontSize: 13, fontWeight: 700, color: '#1B2B4B', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', background: '#f6f8fb', padding: '2px 8px', borderRadius: 6 },
  publicIdCopy: { background: 'none', border: '1px solid #d7dee8', borderRadius: 8, color: '#149e90', fontSize: 11, fontWeight: 600, padding: '4px 9px', cursor: 'pointer' },
  badges: { display: 'flex', gap: 8 },
  badgeTeal: { background: '#2EC4B6', color: '#fff', padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
  badgeCheck: { background: '#e9faf7', color: '#149e90', padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' },
  gridMobile: { gridTemplateColumns: '1fr' },
  card: { background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 4px 16px rgba(27,43,75,0.06)' },
  cardTitle: { fontSize: 17, fontWeight: 700, color: '#1B2B4B', marginBottom: 16 },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f4f7fb' },
  rowLabel: { fontSize: 13, color: '#64748b' },
  rowValue: { fontSize: 14, color: '#1B2B4B', fontWeight: 500 },
  inlineInput: { background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', borderRadius: 8, padding: '6px 10px', fontSize: 14, outline: 'none', maxWidth: 200, textAlign: 'right' },
  taxRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 },
  taxOpt: { border: '2px solid #eef2f7', borderRadius: 10, padding: '10px', textAlign: 'center', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#64748b' },
  taxOptActive: { border: '2px solid #2EC4B6', background: '#f0fffe', color: '#149e90' },
  note: { fontSize: 12, color: '#94a3b8', marginTop: 12 },
  error: { background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginTop: 12 },
  toggleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' },
  btnGhostFull: { display: 'block', textAlign: 'center', background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', borderRadius: 10, padding: '11px', fontSize: 14, cursor: 'pointer', textDecoration: 'none', marginBottom: 10 },
  btnLogout: { width: '100%', background: 'transparent', color: '#e5484d', border: '1px solid #fecaca', borderRadius: 10, padding: '11px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  badgeGray: { background: '#f1f5f9', color: '#64748b', padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
  badgeYellow: { background: '#fffbeb', color: '#b45309', padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
  badgeRedSoft: { background: '#fef2f2', color: '#dc2626', padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
  errorSoft: { background: '#fef2f2', color: '#dc2626', padding: '8px 12px', borderRadius: 8, fontSize: 12, marginTop: 8 },
  link: { color: '#149e90', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  pendingBanner: { background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 10, padding: '12px 16px', marginBottom: 16 },
  rejectedBanner: { background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13 },
  bannerClose: { background: 'transparent', border: 'none', color: '#dc2626', fontSize: 20, lineHeight: 1, cursor: 'pointer', padding: 0, flexShrink: 0 },
  successBanner: { background: '#e9faf7', border: '1px solid #a7f3d0', color: '#149e90', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginTop: 12 },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalCard: { background: '#fff', borderRadius: 16, padding: 24, width: 360, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  modalInput: { background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', borderRadius: 8, padding: '8px 10px', fontSize: 14, outline: 'none' },
};
